import { ng } from '@uirouter/angularjs/lib/angular';
import { StateParams, StateService } from '@uirouter/core';
import angular = require('angular');
import { NotificationService } from '../../../../app.common/modules/common/services/notification.service';
import { PopupService } from '../../../../app.common/modules/common/services/popup.service';
import { AccountService } from '../../account/account.service';
import { RetailerService } from '../../api/retailer.service';
import { ServicesService } from '../../common/services/services.service';
import { IServiceEditModel, IServicePrice, IServicesModel } from '../../model/services/IServicesModel';

class AddOrEditServices implements ng.IController {
    public static $inject = [
        '$translate',
        'notificationService',
        'servicesService',
        'accountService',
        'popupService',
        '$state',
        '$stateParams',
        'retailerService',
        '$uibModal'
    ];

    public idParam: string;
    public modalInstance: ng.ui.bootstrap.IModalInstanceService = this as any;
    public waiting: boolean;
    public isNew: boolean;
    public showMinPriceError: boolean = false;
    public showMaxPriceError: boolean = false;
    public showErrors: boolean = false;
    public serviceForm: ng.IFormController;
    public toolbar = [
        ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
        ['justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull'],
        ['bold', 'italics', 'underline', 'strikeThrough', 'ul', 'ol', 'html']
    ];
    public imageSrc: string;
    public giftAidVisible = false;

    public currencyCssClass: string;

    private item: IServiceEditModel = {
        Title: null,
        Description: null,
        PriceType: 0,
        Price: null,
        MinPrice: null,
        DefaultPrice: null,
        MaxPrice: null,
        AllowToEditPriceByShopper: false,
        GiftAid: false,
        FeeRecipient: 0,
        DisplayedInReceipt: false,
        LinkTitle: null,
    };

    private priceItem: IServicePrice = {
        FixedPrice: 0,
        EditablePrice: {
            MinPrice: 0,
            DefaultPrice: 0,
            MaxPrice: 0
        },
        CurrencyCode: null,
        CurrencySign: null
    };

    private readModel: IServicesModel = null;

    private minSize = {
        w: 1032,
        h: 702
    };
    private maxFileSize = 5;
    private fileExtensions = ['jpeg', 'jpg', 'png', 'bmp', 'gif'];

    constructor(
        public $translate: angular.translate.ITranslateService,
        public notificationService: NotificationService,
        public servicesService: ServicesService,
        private accountService: AccountService,
        public popupService: PopupService,
        private $state: StateService,
        public $stateParams: StateParams,
        private retailerService: RetailerService,
        private $uibModal: ng.ui.bootstrap.IModalService
    ) {
    }

    public async $onInit() {
        this.idParam = this.$stateParams.id;
        this.isNew = !this.idParam;
        if (this.idParam) {
            await this.loadServiceItem();
        } else {
            this.setCurrency();
        }
        // 1 - UK
        this.giftAidVisible = this.accountService.userData.countryID === 1;
    }

    public async loadServiceItem() {
        try {
            this.waiting = true;
            this.notificationService.showWaitingMessage();
            this.readModel = await this.servicesService.getService(this.idParam);
            this.item = this.transform(this.readModel);
            this.imageSrc = this.item.ImageID ? this.retailerService.serviceImage(this.item.ImageID, 128, 128) : null;
            this.priceItem = this.readModel.Price;
            this.currencyCssClass = this.getCurrencyCssClass(this.priceItem.CurrencySign);
            this.notificationService.clearAll();
        } catch (error) {
            this.notificationService.clearAll();
            this.notificationService.showErrorMessage(error);
        } finally {
            this.waiting = false;
        }
    }

    public setCurrency() {
        this.currencyCssClass = this.getCurrencyCssClass(this.accountService.userData.currencySign);
    }

    public getCurrencyCssClass(sign: string): string {
        switch (sign) {
            case '£':
                return 'withPound';
            case 'kr':
                return 'withKrona';
            case '$':
                return 'withDollar';
            case '€':
                return 'withEuro';
            case 'د.إ':
                return 'withDirham';
            default:
                return '';
        }
    }

    public async showEditablePriceInfo() {
        try {
            await this.popupService.confirm({
                classPopup: 'popup--redesign modal360',
                title: this.$translate.instant('service_editable_price_title'),
                content: this.$translate.instant('service_editable_price_content'),
                okClass: 'btn btn-success w80',
                okTxt: this.$translate.instant('ok'),
                noCancelButton: true
            });
        } catch (e) {
            return;
        }
    }

    public async showGiftAidInfo() {
        try {
            await this.popupService.confirm({
                classPopup: 'popup--redesign modal360',
                title: this.$translate.instant('service_gift_aid_title'),
                content: this.$translate.instant('service_gift_aid_content'),
                okClass: 'btn btn-success w80',
                okTxt: this.$translate.instant('ok'),
                noCancelButton: true
            });
        } catch (e) {
            return;
        }
    }

    public createService() {
        this.validateFixedPrice();
        this.validatePrice();
        this.validateMinPrice();
        this.validateMaxPrice();
        if (this.serviceForm.$invalid) {
            this.showErrors = true;
            window.scrollTo(0, 0);
        } else {
            this.showErrors = false;
            this.notificationService.showWaitingMessage();
            this.item.DefaultPrice = this.item.PriceType === 1 ? this.item.Price : 0;
            this.servicesService.saveService(this.item, this.idParam)
              .then(() => {
                  this.notificationService.clearAll();
                  this.$state.go('app.activeServices', {isActive: true});
              }).catch((error) => {
                this.notificationService.clearAll();
                this.notificationService.showErrorMessage(error);
            });
        }
    }

    public saveButtonDisabled() {
        return !(this.item.Title && this.item.Description);
    }

    public discard() {
        this.$state.go('app.activeServices', {isActive: true});
    }

    public async uploadLogo() {
        if (this.waiting) {
            return;
        }
        try {
            const image = await this.$uibModal.open({
                component: 'jispPayAvatar',
                resolve: {
                    avatarId: () => this.item ? this.item.ImageID : null,
                    title: () => this.$translate.instant('service_image'),
                    sizeText: () => this.$translate.instant('store_photo_min_size_hint', {width: this.minSize.w, height: this.minSize.h}),
                    getUrl: () => this.item && this.item.ImageID ? this.retailerService.serviceImage(this.item.ImageID) : null,
                    imageSize: () => this.minSize,
                    maxFileSizeMb: () => this.maxFileSize,
                    disableAspectRatio: () => true,
                    fileExtensions: () => this.fileExtensions
                },
                backdrop: 'static',
                windowClass: 'crop-image-dialog'
            }).result;
            if (image) {
                this.serviceForm.$setDirty();
                await this.saveImage(image);
            }
        } catch (error) {
            return;
        }
    }

    public removeImage() {
        this.imageSrc = '';
        this.item.ImageID = null;
    }

    private async saveImage(imageFileData: string) {
        try {
            this.item.ImageID = await this.servicesService.uploadServiceImage(imageFileData);
            this.imageSrc = this.retailerService.serviceImage(this.item.ImageID, 128, 128);
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
    }

    private validateFixedPrice() {
        if (this.item.PriceType === 0 && this.serviceForm.price) {
            if (this.item.Price === null || this.item.Price === 0 || !this.item.Price) {
                this.serviceForm.price.$setValidity('fixedPrice', false);
            } else {
                this.serviceForm.price.$setValidity('fixedPrice', true);
            }
        }
    }

    private validatePrice() {
        if (this.item.PriceType === 1) {
            if (this.serviceForm.minPrice) {
                this.serviceForm.minPrice.$setValidity('minPrice', this.priceValid(this.item.MinPrice));
            }
            if (this.serviceForm.defaultPrice) {
                this.serviceForm.defaultPrice.$setValidity('minPrice', this.priceValid(this.item.Price));
            }
            if (this.serviceForm.maxPrice) {
                this.serviceForm.maxPrice.$setValidity('minPrice', this.priceValid(this.item.MaxPrice));
            }
        }
    }

    private validateMinPrice() {
        if (this.item.PriceType === 1 && this.serviceForm.minPrice) {
            if (this.item.MinPrice > this.item.Price) {
                this.serviceForm.minPrice.$setValidity('minPriceLower', false);
            } else {
                this.serviceForm.minPrice.$setValidity('minPriceLower', true);
            }
        }
    }

    private validateMaxPrice() {
        if (this.item.PriceType === 1 && this.serviceForm.maxPrice) {
            if (this.item.MaxPrice && this.item.MaxPrice < this.item.Price ) {
                this.serviceForm.maxPrice.$setValidity('maxPriceLower', false);
            } else if (!this.item.MaxPrice) {
                this.serviceForm.maxPrice.$setValidity('maxPriceLower', true);
            } else {
                this.serviceForm.maxPrice.$setValidity('maxPriceLower', true);
            }
        }
    }

    private priceValid(price: number) {
        return price == null || parseFloat(price.toString()) !== 0;
    }

    private transform(readModel: IServicesModel): IServiceEditModel {
        return {
            Title: readModel.Title,
            Description: readModel.Description,
            PriceType: readModel.PriceType,
            Price: readModel.Price.FixedPrice || readModel.Price.EditablePrice.DefaultPrice || 0,
            MinPrice: readModel.Price.EditablePrice ? readModel.Price.EditablePrice.MinPrice : null,
            DefaultPrice: readModel.Price.EditablePrice ? readModel.Price.EditablePrice.DefaultPrice : 0,
            MaxPrice: readModel.Price.EditablePrice ? readModel.Price.EditablePrice.MaxPrice : null,
            AllowToEditPriceByShopper: readModel.AllowToEditPriceByShopper,
            GiftAid: readModel.GiftAid,
            FeeRecipient: readModel.FeeRecipient,
            DisplayedInReceipt: readModel.DisplayedInReceipt,
            LinkTitle: readModel.LinkTitle,
            ImageID: readModel.ImageID
        } as IServiceEditModel;
    }
}

angular.module('naut').component('addOrEditService', {
    template: require('./add-or-edit-service.component.html'),
    controller: AddOrEditServices,
    controllerAs: 'ctrl',
    bindings: {
        resolve: '<',
        close: '&',
        dismiss: '&'
    }
});



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/services/add-or-edit-service/add-or-edit-service.component.ts